import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';
import { saveThemePreference } from '../services/themePreferenceService';
import { browserStorage, isThemePreference, readTheme, resolveTheme, THEME_KEY, writeTheme, type ThemePreference } from './preference';

type ThemeState = { preference: ThemePreference; resolved: 'light' | 'dark'; saving: boolean; saveFailed: boolean; choose: (value: ThemePreference) => Promise<void> };
const ThemeContext = createContext<ThemeState | null>(null);

/** Account identity is deliberately independent of the effective tenant. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [preference, setPreference] = useState<ThemePreference>(() => readTheme(browserStorage()));
  const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const identity = useRef<string | null>(null);
  const revision = useRef(0);
  const latestSave = useRef(new Map<string, number>());
  const resolved = resolveTheme(preference, systemDark);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystemDark(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const accountId = user?.id ?? null;
  const remote = user?.user_metadata?.collectr_operator_theme;
  useLayoutEffect(() => {
    if (loading) return;
    if (identity.current !== accountId) {
      revision.current++;
      setSaving(false);
    }
    identity.current = accountId;
    const key = accountId ? `${THEME_KEY}:${accountId}` : `${THEME_KEY}:anonymous`;
    const stored = readTheme(browserStorage(), key);
    let pending = false;
    try { pending = browserStorage()?.getItem(`${key}:pending`) === 'true'; } catch { /* Storage unavailable. */ }
    const next = accountId && isThemePreference(remote) && !pending ? remote : stored;
    setSaveFailed(pending);
    setPreference(next);
    // A cache for the very first paint; never used as another account's preference.
    writeTheme(browserStorage(), THEME_KEY, next);
  }, [accountId, remote, loading]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    document.documentElement.dataset.theme = resolved;
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  async function choose(value: ThemePreference) {
    if (saving) return;
    const currentRevision = ++revision.current;
    setSaveFailed(false);
    setPreference(value);
    const storage = browserStorage();
    writeTheme(storage, accountId ? `${THEME_KEY}:${accountId}` : `${THEME_KEY}:anonymous`, value);
    writeTheme(storage, THEME_KEY, value);
    if (!accountId) return;
    latestSave.current.set(accountId, currentRevision);
    try { storage?.setItem(`${THEME_KEY}:${accountId}:pending`, 'true'); } catch { /* In-memory fallback. */ }
    setSaving(true);
    try {
      await saveThemePreference(value);
      // An earlier account session must not clear a newer failed choice's fallback.
      if (latestSave.current.get(accountId) === currentRevision) {
        try { storage?.removeItem(`${THEME_KEY}:${accountId}:pending`); } catch { /* Storage unavailable. */ }
      }
      if (currentRevision === revision.current) setSaveFailed(false);
    } catch {
      if (currentRevision === revision.current) {
        setSaveFailed(true);
        toast.error('Thema toegepast. Opslaan in je account is mislukt. Je kunt opnieuw opslaan via het gebruikersmenu.');
      }
    } finally {
      if (currentRevision === revision.current) setSaving(false);
    }
  }
  return <ThemeContext.Provider value={{ preference, resolved, saving, saveFailed, choose }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme requires ThemeProvider');
  return context;
}
